import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { WizardFooter, WizardModal } from '~/components/overlays';
import { useCreateBuyList, useFetchOpenBuyListTargets } from '~/api/buyList';
import {
  AddToBuyListCreateForm,
  type AddToBuyListDraft,
} from '~/pages/buyer-inventory/sections/AddToBuyListCreateForm';
import toast from 'react-hot-toast';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';
import { useSubmitOnce } from '~/hooks/useSubmitOnce';
import {
  BuyListItemsStepsFooter,
  BuyListStepContent,
  type BuyListItemsStep,
} from './BuyListItemsSteps';

const STEPS = ['details', 'products', 'review'] as const;

type Wizard =
  | { step: 'details'; buyListId: string | null }
  | { step: BuyListItemsStep; buyListId: string };

const DUPLICATE_BUY_LIST_MESSAGE =
  'You already have an open buy list for this location with the same "Ship week of" date.';

type CreateBuyListModalProps = {
  onClose: () => void;
};

export function CreateBuyListModal({
  onClose,
}: Readonly<CreateBuyListModalProps>) {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const [wizard, setWizard] = useState<Wizard>({
    step: 'details',
    buyListId: null,
  });
  const [draft, setDraft] = useState<AddToBuyListDraft | null>(null);
  const [isSavingItems, setIsSavingItems] = useState(false);
  const [hasPendingPick, setHasPendingPick] = useState(false);
  const { mutate: createBuyList, isPending: isCreating } = useCreateBuyList();
  const isSaving = isCreating || isSavingItems;
  const submitOnce = useSubmitOnce();
  const fetchOpenBuyListTargets = useFetchOpenBuyListTargets();

  const handleDraftChange = useCallback(
    (nextDraft: AddToBuyListDraft | null) => setDraft(nextDraft),
    []
  );

  const goNext = () => {
    if (wizard.buyListId) {
      setWizard({ step: 'products', buyListId: wizard.buyListId });
      return;
    }
    if (!draft) return;
    const note = draft.note.trim();
    submitOnce((onSettled) =>
      createBuyList(
        {
          locationId: draft.locationId,
          transportVehicleId: draft.transportVehicleId,
          expectedShipDate: draft.expectedShipDate,
          note: note === '' ? null : note,
        },
        {
          onSuccess: (result) =>
            setWizard({ step: 'products', buyListId: result.createBuyList.id }),
          onError: (error) => void toastCreateError(error, draft),
          onSettled,
        }
      )
    );
  };

  const goToStep = (step: Wizard['step'], buyListId: string) => {
    setHasPendingPick(false);
    setWizard({ step, buyListId });
  };

  const openBuyList = (id: string) => {
    onClose();
    void navigate(`/buy-lists/view/${id}`);
  };

  const findExistingBuyList = async (current: AddToBuyListDraft) => {
    try {
      const { myBuyLists } = await fetchOpenBuyListTargets();
      return myBuyLists.content.find(
        (list) =>
          list.location.id === current.locationId &&
          list.expectedShipDate === current.expectedShipDate
      );
    } catch {
      return undefined;
    }
  };

  const toastCreateError = async (
    error: unknown,
    current: AddToBuyListDraft
  ) => {
    const message = extractGraphQLErrorMessages(error as ClientError).join(
      ', '
    );
    const existing =
      message === DUPLICATE_BUY_LIST_MESSAGE
        ? await findExistingBuyList(current)
        : undefined;
    toast.error(
      existing ? (
        <span>
          {message}{' '}
          <button
            type="button"
            className="underline"
            onClick={() => openBuyList(existing.id)}
            data-testid="create-buy-list-view-existing"
          >
            {t('inventory.buy_list.create_view_existing')}
          </button>
        </span>
      ) : (
        message
      )
    );
  };

  const footer =
    wizard.step === 'details' ? (
      <WizardFooter
        end={
          <Button
            variant="primary"
            width="auto"
            disabled={isSaving || (!wizard.buyListId && !draft)}
            onClick={goNext}
            data-testid="create-buy-list-next"
          >
            {t('inventory.buy_list.create_next')}
          </Button>
        }
      />
    ) : (
      <BuyListItemsStepsFooter
        step={wizard.step}
        isSaving={isSaving}
        hasPendingPick={hasPendingPick}
        testIdPrefix="create-buy-list"
        onStepChange={(step) => goToStep(step, wizard.buyListId)}
        onBackFromProducts={() => goToStep('details', wizard.buyListId)}
        onDone={() => openBuyList(wizard.buyListId)}
      />
    );

  return (
    <WizardModal
      testIdPrefix="create-buy-list"
      testId="create-buy-list"
      onClose={onClose}
      isPending={isSaving}
      steps={STEPS.map((id) => ({
        id,
        label: t(`inventory.buy_list.create_step_${id}`),
        title: t(`inventory.buy_list.create_step_${id}_title`),
        description: t(`inventory.buy_list.create_step_${id}_subtitle`),
      }))}
      currentStepId={wizard.step}
      stepIndicatorTestId="create-buy-list-steps"
      stepIndicatorAriaLabel={t('inventory.buy_list.steps_label')}
      footer={footer}
    >
      {wizard.buyListId ? (
        <BuyListStepContent
          buyListId={wizard.buyListId}
          step={wizard.step}
          onSavingChange={setIsSavingItems}
          onPendingPickChange={setHasPendingPick}
        />
      ) : (
        <AddToBuyListCreateForm
          initialLocationId=""
          onDraftChange={handleDraftChange}
        />
      )}
    </WizardModal>
  );
}
